import type { HotKey } from 'is-hotkey';
import { parseHotkey } from 'is-hotkey';
import { useEffect, useState } from 'react';

/**
 * Hook that tracks if a specific key or keys are currently pressed.
 * @param hotkey - A string representing the key or keys to track (e.g., 'mod+s', 'shift+enter').
 * @param initial - Initial state, defaults to false.
 * @returns A boolean indicating whether any of the specified keys are currently pressed.
 */
export function useIsKeyPressed(hotkey: string, initial = false): boolean {
    const [pressed, setPressed] = useState(initial);

    useEffect(() => {
        const parsed = parseHotkey(hotkey, { byKey: true });
        const pressedKeys = new Set<string>();
        function downHandler(event: KeyboardEvent) {
            pressedKeys.add(event.key.toLowerCase());
            setPressed(checkHotkey(pressedKeys, event, parsed));
        }

        const upHandler = (event: KeyboardEvent) => {
            pressedKeys.delete(event.key.toLowerCase());
            setPressed(checkHotkey(pressedKeys, event, parsed));
        };

        window.addEventListener('keydown', downHandler);
        window.addEventListener('keyup', upHandler);
        return () => {
            window.removeEventListener('keydown', downHandler);
            window.removeEventListener('keyup', upHandler);
        };
    }, [hotkey]);

    return pressed;
}

function checkHotkey(pressedKeys: Set<string>, event: KeyboardEvent, hotkey: HotKey): boolean {
    if (hotkey.altKey && !event.altKey) {
        return false;
    }
    if (hotkey.ctrlKey && !event.ctrlKey) {
        return false;
    }
    if (hotkey.metaKey && !event.metaKey) {
        return false;
    }
    if (hotkey.shiftKey && !event.shiftKey) {
        return false;
    }
    if (hotkey.key && !pressedKeys.has(hotkey.key)) {
        return false;
    }
    return true;
}
